import type { Optional } from '@festina-life/core'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { ActiveConfigurationItem } from './types'

export const useHoveredConfigurationItem = (delayMs = 100) => {
  const timeoutRef = useRef<Optional<number>>(undefined)
  const [hoveredConfigurationItem, setHoveredConfigurationItem] =
    useState<Optional<ActiveConfigurationItem>>()

  const clearPendingHover = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = undefined
    }
  }, [])

  const startHoveredConfigurationItem = useCallback(
    (nextHoveredConfigurationItem: Optional<ActiveConfigurationItem>) => {
      clearPendingHover()

      if (!nextHoveredConfigurationItem) {
        setHoveredConfigurationItem(undefined)
        return
      }

      timeoutRef.current = window.setTimeout(() => {
        setHoveredConfigurationItem(nextHoveredConfigurationItem)
        timeoutRef.current = undefined
      }, delayMs)
    },
    [clearPendingHover, delayMs],
  )

  const clearHoveredConfigurationItem = useCallback(() => {
    clearPendingHover()
    setHoveredConfigurationItem(undefined)
  }, [clearPendingHover])

  useEffect(
    () => () => {
      clearPendingHover()
    },
    [clearPendingHover],
  )

  return {
    hoveredConfigurationItem,
    startHoveredConfigurationItem,
    clearHoveredConfigurationItem,
  }
}
